import { useEffect } from "react";
import {
  Title,
  SimpleGrid,
  Stack,
  Box,
  Group,
  Button,
  Modal,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";
import type { UseFormReturnType } from "@mantine/form";
import { CertaintyTextInput, CertaintySelect } from "./CertaintyInput";
import SmartSuggestion from "./SmartSuggestion";
import {
  useStates,
  useCities,
  useInsurancePlanTypesByProvider,
} from "../../hooks/useReferenceData";
import InsuranceProviderForm, {
  type InsuranceProviderFormValues,
} from "../admin/insurance-providers/InsuranceProviderForm";
import { useCreateInsuranceProvider } from "../../hooks/admin/useInsuranceProvidersAdmin";

// Utility to filter out options without valid value property
// This prevents Mantine Select validation errors during navigation/hydration
const sanitizeOptions = <T extends { value: string }>(
  options: T[] | undefined | null
): T[] => {
  if (!Array.isArray(options)) return [];
  return options.filter(
    (opt): opt is T =>
      opt != null &&
      typeof opt === "object" &&
      "value" in opt &&
      opt.value != null &&
      opt.value !== ""
  );
};

interface InsuranceFormProps {
  form: UseFormReturnType<any>;
  type: "primary" | "secondary";
  certainties?: Record<string, number>;
  onCertaintyChange?: (field: string, certainty: number) => void;
  insuranceProviders?: Array<{ value: string; label: string }>;
  // These props are kept for backwards compatibility but no longer used
  opened?: boolean;
  onToggle?: () => void;
  getProviderLabel?: (id: string) => string;
  missingFields?: string[];
  unmatchedExtractions?: Record<string, string>;
  onClearUnmatched?: (field: string) => void;
}

export default function InsuranceForm({
  form,
  type,
  certainties = {},
  onCertaintyChange,
  insuranceProviders = [],
  unmatchedExtractions = {},
  onClearUnmatched,
}: InsuranceFormProps) {
  const fieldPrefix =
    type === "primary" ? "case.primaryInsurance" : "case.secondaryInsurance";

  // State and city data
  const { data: states = [] } = useStates();
  const insuranceSection =
    form.values.case?.[
      type === "primary" ? "primaryInsurance" : "secondaryInsurance"
    ];
  const selectedState = insuranceSection?.address?.state;
  const { data: cities = [] } = useCities(selectedState);

  // Get the selected insurance provider ID
  const selectedProviderId = insuranceSection?.insuranceProvider;

  // Get insurance plan types filtered by the selected provider
  // Only fetches when a provider is selected
  const { data: providerPlanTypesRaw = [], isLoading: isPlanTypesLoading } =
    useInsurancePlanTypesByProvider(selectedProviderId);

  const insurancePlanTypes = sanitizeOptions(
    providerPlanTypesRaw.map((planType) => ({
      value: planType.id,
      label: planType.type,
    }))
  );

  // Insurance Provider quick-add modal
  const [
    providerModalOpened,
    { open: openProviderModal, close: closeProviderModal },
  ] = useDisclosure(false);
  const createInsuranceProvider = useCreateInsuranceProvider();

  const handleProviderSubmit = async (values: InsuranceProviderFormValues) => {
    const provider = await createInsuranceProvider.mutateAsync({
      name: values.name,
      naicNumber: values.naicNumber || undefined,
      mailingAddress: values.mailingAddress || undefined,
      city: values.city || undefined,
      state: values.state || undefined,
      zipCode: values.zipCode || undefined,
      phoneNumber: values.phoneNumber || undefined,
      isActive: values.isActive,
    });
    form.setFieldValue(`${fieldPrefix}.insuranceProvider`, provider.id);
    onClearUnmatched?.(`${fieldPrefix}.insuranceProvider`);
    closeProviderModal();
  };

  // Clear plan type if the current value is not in the allowed options for the selected provider
  // This handles: 1) Provider change, 2) Initial form load with invalid data
  const currentPlanType = insuranceSection?.planType;
  useEffect(() => {
    // Skip if no provider selected or plan types are still loading
    if (!selectedProviderId || isPlanTypesLoading) return;

    // Skip if no plan type is currently selected
    if (!currentPlanType) return;

    // Check if the current plan type is valid for the selected provider
    const isValidPlanType = insurancePlanTypes.some(
      (pt) => pt.value === currentPlanType
    );

    // Clear the field if the current value is not in the allowed options
    if (!isValidPlanType && insurancePlanTypes.length > 0) {
      form.setFieldValue(`${fieldPrefix}.planType`, "");
    }
  }, [
    selectedProviderId,
    currentPlanType,
    insurancePlanTypes,
    isPlanTypesLoading,
    fieldPrefix,
    form,
  ]);

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Box>
          <CertaintySelect
            label="Insurance Provider"
            placeholder="Select provider"
            data={sanitizeOptions(insuranceProviders)}
            searchable
            description={
              type === "primary"
                ? "Recommended - will be flagged if missing"
                : undefined
            }
            showReviewBadge={type === "primary"}
            reviewTooltip="Verify insurance provider"
            name={`${fieldPrefix}.insuranceProvider`}
            {...form.getInputProps(`${fieldPrefix}.insuranceProvider`)}
            certainty={certainties[`${fieldPrefix}.insuranceProvider`]}
            onCertaintyChange={(c) =>
              onCertaintyChange?.(`${fieldPrefix}.insuranceProvider`, c)
            }
          />
          {unmatchedExtractions[`${fieldPrefix}.insuranceProvider`] &&
          !insuranceProviders.some(
            (p) => p.value === insuranceSection?.insuranceProvider
          ) ? (
            <SmartSuggestion
              extractedValue={
                unmatchedExtractions[`${fieldPrefix}.insuranceProvider`]
              }
              onAdd={async () => {
                const name =
                  unmatchedExtractions[`${fieldPrefix}.insuranceProvider`];
                const provider = await createInsuranceProvider.mutateAsync({
                  name,
                });
                form.setFieldValue(
                  `${fieldPrefix}.insuranceProvider`,
                  provider.id
                );
                onClearUnmatched?.(`${fieldPrefix}.insuranceProvider`);
              }}
              isLoading={createInsuranceProvider.isPending}
            />
          ) : (
            <Group justify="flex-end" mt={4}>
              <Button
                variant="subtle"
                size="compact-xs"
                leftSection={<IconPlus size={12} />}
                onClick={openProviderModal}
              >
                Add New
              </Button>
            </Group>
          )}
        </Box>

        <CertaintySelect
          label="Insurance Plan Type"
          placeholder={
            selectedProviderId
              ? isPlanTypesLoading
                ? "Loading plan types..."
                : "Select plan type"
              : "Select insurance provider first"
          }
          data={insurancePlanTypes}
          searchable
          disabled={!selectedProviderId || isPlanTypesLoading}
          description={
            type === "primary"
              ? "Recommended - will be flagged if missing"
              : undefined
          }
          showReviewBadge={type === "primary"}
          reviewTooltip="Verify insurance plan type (PPO, HMO, etc.)"
          name={`${fieldPrefix}.planType`}
          {...form.getInputProps(`${fieldPrefix}.planType`)}
          certainty={certainties[`${fieldPrefix}.planType`]}
          onCertaintyChange={(c) =>
            onCertaintyChange?.(`${fieldPrefix}.planType`, c)
          }
        />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <CertaintyTextInput
          label="Policy Number"
          placeholder="Policy #"
          description={
            type === "primary"
              ? "Recommended - will be flagged if missing"
              : undefined
          }
          showReviewBadge={type === "primary"}
          reviewTooltip="Verify insurance policy number"
          name={`${fieldPrefix}.policyNumber`}
          {...form.getInputProps(`${fieldPrefix}.policyNumber`)}
          certainty={certainties[`${fieldPrefix}.policyNumber`]}
          onCertaintyChange={(c) =>
            onCertaintyChange?.(`${fieldPrefix}.policyNumber`, c)
          }
        />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <CertaintyTextInput
          label="Authorization Number"
          placeholder="Auth #"
          {...form.getInputProps(`${fieldPrefix}.authorizationNumber`)}
          certainty={certainties[`${fieldPrefix}.authorizationNumber`]}
          onCertaintyChange={(c) =>
            onCertaintyChange?.(`${fieldPrefix}.authorizationNumber`, c)
          }
        />

        <CertaintyTextInput
          label="Group Number"
          placeholder="Group #"
          showReviewBadge={type === "primary"}
          reviewTooltip="Verify insurance group number"
          {...form.getInputProps(`${fieldPrefix}.groupNumber`)}
          certainty={certainties[`${fieldPrefix}.groupNumber`]}
          onCertaintyChange={(c) =>
            onCertaintyChange?.(`${fieldPrefix}.groupNumber`, c)
          }
        />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <CertaintyTextInput
          label="Group Name"
          placeholder="Group name"
          showReviewBadge={type === "primary"}
          reviewTooltip="Verify insurance group name"
          {...form.getInputProps(`${fieldPrefix}.groupName`)}
          certainty={certainties[`${fieldPrefix}.groupName`]}
          onCertaintyChange={(c) =>
            onCertaintyChange?.(`${fieldPrefix}.groupName`, c)
          }
        />

        <CertaintySelect
          label="Patient Relation to Insured"
          placeholder="Select relation"
          data={[
            { value: "Self", label: "Self" },
            { value: "Spouse", label: "Spouse" },
            { value: "Child", label: "Child" },
            { value: "Other", label: "Other" },
          ]}
          {...form.getInputProps(`${fieldPrefix}.patientRelationToInsured`)}
          certainty={certainties[`${fieldPrefix}.patientRelationToInsured`]}
          onCertaintyChange={(c) => {
            onCertaintyChange?.(`${fieldPrefix}.patientRelationToInsured`, c);
          }}
        />
      </SimpleGrid>
      <Title order={5} mt="xs">
        Insurance Address
      </Title>

      <CertaintyTextInput
        label="Street Address"
        placeholder="123 Main St"
        {...form.getInputProps(`${fieldPrefix}.address.address`)}
        certainty={certainties[`${fieldPrefix}.address.address`]}
        onCertaintyChange={(c) =>
          onCertaintyChange?.(`${fieldPrefix}.address.address`, c)
        }
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <CertaintySelect
          label="State"
          placeholder="Select state"
          data={sanitizeOptions(states)}
          searchable
          {...form.getInputProps(`${fieldPrefix}.address.state`)}
          certainty={certainties[`${fieldPrefix}.address.state`]}
          onCertaintyChange={(c) =>
            onCertaintyChange?.(`${fieldPrefix}.address.state`, c)
          }
          onChange={(value) => {
            form.setFieldValue(`${fieldPrefix}.address.state`, value);
            // Clear city when state changes
            form.setFieldValue(`${fieldPrefix}.address.city`, "");
          }}
        />
        <CertaintySelect
          label="City"
          placeholder="Select city"
          data={sanitizeOptions(cities)}
          searchable
          disabled={!selectedState}
          {...form.getInputProps(`${fieldPrefix}.address.city`)}
          certainty={certainties[`${fieldPrefix}.address.city`]}
          onCertaintyChange={(c) =>
            onCertaintyChange?.(`${fieldPrefix}.address.city`, c)
          }
        />
        <CertaintyTextInput
          label="ZIP Code"
          placeholder="12345"
          {...form.getInputProps(`${fieldPrefix}.address.zipCode`)}
          certainty={certainties[`${fieldPrefix}.address.zipCode`]}
          onCertaintyChange={(c) =>
            onCertaintyChange?.(`${fieldPrefix}.address.zipCode`, c)
          }
        />
      </SimpleGrid>

      {/* Quick-Add Insurance Provider Modal */}
      <Modal
        opened={providerModalOpened}
        onClose={closeProviderModal}
        title="Add New Insurance Provider"
        size="lg"
        closeOnClickOutside={false}
      >
        <InsuranceProviderForm
          onSubmit={handleProviderSubmit}
          onCancel={closeProviderModal}
          isSubmitting={createInsuranceProvider.isPending}
          submitLabel="Create"
        />
      </Modal>
    </Stack>
  );
}
